import {
  keepPreviousData,
  queryOptions,
  useQuery,
  useSuspenseQuery,
  type UseQueryResult,
  type UseSuspenseQueryResult,
} from '@tanstack/react-query'

import { hasCode } from '@/api/errors'
import { courseService } from '@/features/courses/api/courseService'
import { courseKeys } from '@/features/courses/api/keys'
import type {
  Certificates,
  CourseCard,
  CourseDetail,
  CourseHome,
  CoursePaymentRecord,
  CourseReviewsPage,
  DictionaryPage,
  DictionaryQuery,
  DictionaryTerm,
  FaqPage,
  LearningPath,
  MyLearning,
  PageQuery,
  PathStepDetail,
  PurchasedCourse,
  VerifiedCertificate,
} from '@/schemas/courses'

/**
 * The hooks the Courses screens call — one `queryOptions` factory per read
 * (what a route loader hands `ensureRouteData`/`prefetchRouteData`), and the
 * hooks spread it. Factory = key + fetch + staleTime only.
 *
 * NOTHING HERE POLLS. Two freshness classes, set on the factory because
 * `prefetchRouteData` honours the query's OWN staleTime:
 *
 * - CATALOG — courses, categories, reviews, the FAQ, the dictionary, a
 *   verified certificate. Authored in an admin, changed rarely: half an hour.
 * - PROGRESS — every read carrying the member's own progress (a course's
 *   lecture ticks and `purchased`, the home shelves, my-learning, purchases,
 *   payment attempts, certificates, the path). 30s, so the loader's fetch is
 *   not repeated the moment the screen mounts, and the app's
 *   `refetchOnWindowFocus` picks up progress made in another tab or on the
 *   phone once it is older than that.
 *
 * NOT-FOUND IS A VALUE (docs/route-loading.md, "Not-found answers"): the reads
 * whose 404 CODE means "nothing there" resolve that code to `null` in the
 * factory, so the loader never throws the route to its error page for it and
 * the screen keeps its own `data === null` branch. Any other error still
 * throws. Branch on the code, never the message.
 */

const CATALOG_STALE_MS = 30 * 60_000
const PROGRESS_STALE_MS = 30_000
/** A term's example company carries its LIVE last-traded price (mm-be's cron is 60s). */
const TERM_STALE_MS = 60_000

/** mm-be's 404 codes for this feature (`services/courses/exceptions.py`). */
const NOT_FOUND = {
  course: 'COURSE_NOT_FOUND',
  certificate: 'CERTIFICATE_NOT_FOUND',
  path: 'LEARNING_PATH_NOT_FOUND',
  step: 'LEARNING_STEP_NOT_FOUND',
  term: 'DICTIONARY_TERM_NOT_FOUND',
} as const

async function nullOnCode<T>(code: string, read: () => Promise<T>): Promise<T | null> {
  try {
    return await read()
  } catch (error) {
    if (hasCode(error, code)) return null
    throw error
  }
}

// ── Catalog ──────────────────────────────────────────────────────────────────

/** Every catalog course (one request, 200 max). Courses home's grid. */
export const courseListQueryOptions = () =>
  queryOptions<CourseCard[]>({
    queryKey: courseKeys.list(),
    queryFn: () => courseService.list(),
    staleTime: CATALOG_STALE_MS,
  })

export const useSuspenseCourseList = (): UseSuspenseQueryResult<CourseCard[]> =>
  useSuspenseQuery(courseListQueryOptions())

/**
 * The same list, not suspended — My learning's "Pick up next", a shelf the
 * page can live without, started by its loader and drawn with its own
 * skeleton (or nothing, if it fails).
 */
export const useCourseList = (): UseQueryResult<CourseCard[]> => useQuery(courseListQueryOptions())

/**
 * One page of a course's reviews — the detail's Reviews tab, keyed on
 * `?page=`.
 *
 * The previous page stays on screen while the next loads, but only within the
 * SAME course: moving from course A to course B must never draw A's reviews
 * under B's title for a frame, so a placeholder from another course is
 * refused and B shows its own loading state instead.
 */
export const courseReviewsQueryOptions = (courseId: number, query: PageQuery) =>
  queryOptions<CourseReviewsPage>({
    queryKey: courseKeys.reviews(courseId, query),
    queryFn: () => courseService.reviews(courseId, query),
    staleTime: CATALOG_STALE_MS,
  })

export const useCourseReviews = (
  courseId: number,
  query: PageQuery,
): UseQueryResult<CourseReviewsPage> =>
  useQuery({
    ...courseReviewsQueryOptions(courseId, query),
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === courseId ? previous : undefined,
  })

/** One page of the global FAQ, keyed on `?page=`/`?pageSize=`. */
export const courseQuestionsQueryOptions = (query: PageQuery) =>
  queryOptions<FaqPage>({
    queryKey: courseKeys.questions(query),
    queryFn: () => courseService.questions(query),
    staleTime: CATALOG_STALE_MS,
  })

export const useCourseQuestions = (query: PageQuery): UseQueryResult<FaqPage> =>
  useQuery({ ...courseQuestionsQueryOptions(query), placeholderData: keepPreviousData })

// ── A course ─────────────────────────────────────────────────────────────────

/**
 * A course's own page and the lecture player's list — MAIN data for both.
 * `null` for `COURSE_NOT_FOUND` (an unknown, inactive or removed course).
 * Member-aware, so PROGRESS freshness.
 */
export const courseDetailQueryOptions = (courseId: number) =>
  queryOptions<CourseDetail | null>({
    queryKey: courseKeys.detail(courseId),
    queryFn: () => nullOnCode(NOT_FOUND.course, () => courseService.detail(courseId)),
    staleTime: PROGRESS_STALE_MS,
  })

export const useSuspenseCourseDetail = (
  courseId: number,
): UseSuspenseQueryResult<CourseDetail | null> =>
  useSuspenseQuery(courseDetailQueryOptions(courseId))

// ── The member's own ─────────────────────────────────────────────────────────

/** The home shelves; `resume` is the member's. */
export const courseHomeQueryOptions = () =>
  queryOptions<CourseHome>({
    queryKey: courseKeys.home(),
    queryFn: () => courseService.home(),
    staleTime: PROGRESS_STALE_MS,
  })

export const useSuspenseCourseHome = (): UseSuspenseQueryResult<CourseHome> =>
  useSuspenseQuery(courseHomeQueryOptions())

export const myLearningQueryOptions = () =>
  queryOptions<MyLearning>({
    queryKey: courseKeys.myLearning(),
    queryFn: () => courseService.myLearning(),
    staleTime: PROGRESS_STALE_MS,
  })

export const useSuspenseMyLearning = (): UseSuspenseQueryResult<MyLearning> =>
  useSuspenseQuery(myLearningQueryOptions())

export const purchasedCoursesQueryOptions = () =>
  queryOptions<PurchasedCourse[]>({
    queryKey: courseKeys.purchased(),
    queryFn: () => courseService.purchased(),
    staleTime: PROGRESS_STALE_MS,
  })

export const usePurchasedCourses = (): UseQueryResult<PurchasedCourse[]> =>
  useQuery(purchasedCoursesQueryOptions())

/**
 * The member's course payment attempts, newest first — abandoned Pending rows
 * and Cancelled ones included, because "did that go through?" is what the
 * list answers. PROGRESS freshness: a payment mm-be's sweeper settles in the
 * background shows on the next window focus.
 */
export const coursePaymentsQueryOptions = () =>
  queryOptions<CoursePaymentRecord[]>({
    queryKey: courseKeys.payments(),
    queryFn: () => courseService.listCoursePayments(),
    staleTime: PROGRESS_STALE_MS,
  })

/**
 * `enabled` false reads nothing: the payment return asks only for a course
 * attempt's own history row, and a plan attempt has none.
 */
export const useCoursePayments = ({
  enabled = true,
}: { enabled?: boolean | undefined } = {}): UseQueryResult<CoursePaymentRecord[]> =>
  useQuery({ ...coursePaymentsQueryOptions(), enabled })

export const certificatesQueryOptions = () =>
  queryOptions<Certificates>({
    queryKey: courseKeys.certificates(),
    queryFn: () => courseService.certificates(),
    staleTime: PROGRESS_STALE_MS,
  })

export const useSuspenseCertificates = (): UseSuspenseQueryResult<Certificates> =>
  useSuspenseQuery(certificatesQueryOptions())

/**
 * The same read, not suspended — My learning's Certificates figure, a cell
 * the page can live without, started by its loader.
 */
export const useCertificates = (): UseQueryResult<Certificates> =>
  useQuery(certificatesQueryOptions())

/**
 * A certificate confirmed against mm-be's records — PUBLIC. `null` for
 * `CERTIFICATE_NOT_FOUND`: a code mm-be never issued is the page's answer,
 * not a failure.
 */
export const verifyCertificateQueryOptions = (code: string) =>
  queryOptions<VerifiedCertificate | null>({
    queryKey: courseKeys.verify(code),
    queryFn: () => nullOnCode(NOT_FOUND.certificate, () => courseService.verifyCertificate(code)),
    staleTime: CATALOG_STALE_MS,
  })

export const useSuspenseVerifiedCertificate = (
  code: string,
): UseSuspenseQueryResult<VerifiedCertificate | null> =>
  useSuspenseQuery(verifyCertificateQueryOptions(code))

// ── Learning path ────────────────────────────────────────────────────────────

/**
 * The active path. `null` for `LEARNING_PATH_NOT_FOUND` — what dev answers
 * today, and what any environment answers until a path is authored: the
 * screen's "on its way" state, and no Home card.
 */
export const learningPathQueryOptions = () =>
  queryOptions<LearningPath | null>({
    queryKey: courseKeys.path(),
    queryFn: () => nullOnCode(NOT_FOUND.path, () => courseService.path()),
    staleTime: PROGRESS_STALE_MS,
  })

export const useSuspenseLearningPath = (): UseSuspenseQueryResult<LearningPath | null> =>
  useSuspenseQuery(learningPathQueryOptions())

/**
 * The same read, not suspended — the Home card's path teaser and
 * PathStepScreen's secondary "Step N of M" read, neither of which blocks a
 * page. (`/courses` itself now awaits this in its loader, so its teaser
 * reads `useSuspenseLearningPath` instead.)
 */
export const useLearningPath = (): UseQueryResult<LearningPath | null> =>
  useQuery(learningPathQueryOptions())

/** One step. `null` for `LEARNING_STEP_NOT_FOUND`. */
export const pathStepQueryOptions = (stepId: number) =>
  queryOptions<PathStepDetail | null>({
    queryKey: courseKeys.pathStep(stepId),
    queryFn: () => nullOnCode(NOT_FOUND.step, () => courseService.pathStep(stepId)),
    staleTime: PROGRESS_STALE_MS,
  })

export const useSuspensePathStep = (
  stepId: number,
): UseSuspenseQueryResult<PathStepDetail | null> => useSuspenseQuery(pathStepQueryOptions(stepId))

// ── Dictionary ───────────────────────────────────────────────────────────────

/**
 * One page of terms for a search. Keyed on the normalised query
 * (`dictionaryQuery` in the route's loader module builds it for both the
 * loader and the screen), so the previous results dim rather than blank while
 * the member types.
 */
export const dictionaryQueryOptions = (query: DictionaryQuery) =>
  queryOptions<DictionaryPage>({
    queryKey: courseKeys.dictionary(query),
    queryFn: () => courseService.dictionary(query),
    staleTime: CATALOG_STALE_MS,
  })

export const useDictionary = (query: DictionaryQuery): UseQueryResult<DictionaryPage> =>
  useQuery({ ...dictionaryQueryOptions(query), placeholderData: keepPreviousData })

/** One term. `null` for `DICTIONARY_TERM_NOT_FOUND`. */
export const dictionaryTermQueryOptions = (slug: string) =>
  queryOptions<DictionaryTerm | null>({
    queryKey: courseKeys.dictionaryTerm(slug),
    queryFn: () => nullOnCode(NOT_FOUND.term, () => courseService.dictionaryTerm(slug)),
    staleTime: TERM_STALE_MS,
  })

export const useSuspenseDictionaryTerm = (
  slug: string,
): UseSuspenseQueryResult<DictionaryTerm | null> =>
  useSuspenseQuery(dictionaryTermQueryOptions(slug))
